import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import React, { Suspense, lazy, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import UXLayer from "./components/ui/UXLayer";
import CommandPalette from "./components/ui/CommandPalette";

/* ── Dismiss the HTML splash screen once React has mounted ───────────────── */
function SplashDismisser() {
  useEffect(() => {
    const el = document.getElementById("vm-loading");
    if (el) {
      el.classList.add("app-loaded");
      // Remove from DOM after fade transition completes (0.55s)
      const t = setTimeout(() => el.remove(), 600);
      return () => clearTimeout(t);
    }
  }, []);
  return null;
}

/* ── Page transition wrapper ─────────────────────────────────────────────── */
const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 14, filter: "blur(4px)" }}
    animate={{ opacity: 1, y: 0,  filter: "blur(0px)" }}
    exit={{    opacity: 0, y: -8, filter: "blur(2px)" }}
    transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

// Loading Spinner - Lightweight
const PageLoader = () => (
  <div className="min-h-screen bg-[#030712] flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      <span className="text-gray-400 text-sm">Loading...</span>
    </div>
  </div>
);

// Lazy load all pages for better mobile performance
const HomePage = lazy(() => import("./pages/HomePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const HealthNexusPage = lazy(() => import("./pages/HealthNexusPage"));
const AcademyPage = lazy(() => import("./pages/AcademyPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const UserAgreement = lazy(() => import("./pages/UserAgreement"));
const CopyrightPolicy = lazy(() => import("./pages/CopyrightPolicy"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const CustomSolutionsPage = lazy(() => import("./pages/CustomSolutionsPage"));
const PressPage = lazy(() => import("./pages/PressPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const InvestorsPage = lazy(() => import("./pages/InvestorsPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const HRDashboard = lazy(() => import("./pages/HRDashboard"));
const PortalDashboard = lazy(() => import("./pages/PortalDashboard"));
const PortalRMS = lazy(() => import("./pages/PortalRMS"));
const AdminContentManager = lazy(() => import("./pages/AdminContentManager"));
const HealthNexusPortal = lazy(() => import("./pages/HealthNexusPortal"));
const AcademyPortal = lazy(() => import("./pages/AcademyPortal"));
const ClientAdminDashboard = lazy(() => import("./pages/ClientAdminDashboard"));

// New Portal Feature Pages
const LeaveManagementPage   = lazy(() => import("./pages/LeaveManagementPage"));
const AnnouncementsPage     = lazy(() => import("./pages/AnnouncementsPage"));
const DocumentVaultPage     = lazy(() => import("./pages/DocumentVaultPage"));
const ExpenseClaimsPage     = lazy(() => import("./pages/ExpenseClaimsPage"));
const SupportTicketsPage    = lazy(() => import("./pages/SupportTicketsPage"));
const BillingPage           = lazy(() => import("./pages/BillingPage"));
const PlatformAnalyticsPage = lazy(() => import("./pages/PlatformAnalyticsPage"));
const AuditLogPage          = lazy(() => import("./pages/AuditLogPage"));
const OrgChartPage          = lazy(() => import("./pages/OrgChartPage"));

// AEGIS-X Security Platform Pages
const AegisXDashboard = lazy(() => import("./pages/AegisXDashboard"));
const AegisXDevices = lazy(() => import("./pages/AegisXDevices"));
const AegisXThreats = lazy(() => import("./pages/AegisXThreats"));
const AegisXIncidents = lazy(() => import("./pages/AegisXIncidents"));
const AegisXPredator = lazy(() => import("./pages/AegisXPredator"));

// Autonomous Agents Pages
const AgentsOverview = lazy(() => import("./pages/AgentsOverview"));
const AgentsWorkforce = lazy(() => import("./pages/AgentsWorkforce"));
const AgentsApprovals = lazy(() => import("./pages/AgentsApprovals"));
const AgentsActivity = lazy(() => import("./pages/AgentsActivity"));
const TheDelegation = lazy(() => import("./pages/TheDelegation"));

// Projects Pages
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailsPage = lazy(() => import("./pages/ProjectDetailsPage"));
const APIReferencePage = lazy(() => import("./pages/APIReferencePage"));
const StatusPage = lazy(() => import("./pages/StatusPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));

// AegisOS Documentation Pages
const AegisOSQuickstart = lazy(() => import("./pages/AegisOSQuickstart"));
const AegisOSCommandRef = lazy(() => import("./pages/AegisOSCommandRef"));

const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Health Nexus Patient / Doctor / Payment Pages
const PatientDashboard  = lazy(() => import("./pages/PatientDashboard"));
const DoctorDashboard   = lazy(() => import("./pages/DoctorDashboard"));
const AIDoctorChat      = lazy(() => import("./pages/AIDoctorChat"));
const PaymentSuccess    = lazy(() => import("./pages/PaymentSuccess"));

// Components
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import CookieBanner from "./components/CookieBanner.jsx";
import MaintenancePopup from "./components/MaintenancePopup.jsx";
import PagePopupManager from "./components/PagePopupManager.jsx";
import GoogleAdSense from "./components/GoogleAdSense.jsx";

// Smart redirect for /agents - sends admin/client users to agents overview
const AgentsSmartRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user && (user.role === "admin" || user.role === "client")) {
    return <Navigate to="/agents/overview" replace />;
  }
  return <Navigate to="/" replace />;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === "admin")   return <Navigate to="/admin" replace />;
    if (user.role === "hr")      return <Navigate to="/portal/hr" replace />;
    if (user.role === "patient") return <Navigate to="/patient" replace />;
    if (user.role === "doctor")  return <Navigate to="/doctor" replace />;
    return <Navigate to="/rms" replace />;
  }

  return children;
};

/* ── Inner router component — needs useLocation which requires BrowserRouter ─ */
function AppRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <PageTransition key={location.pathname}>
        <Suspense fallback={<PageLoader />}>
          <Routes location={location}>
              {/* Public Corporate Website */}
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/services" element={<CustomSolutionsPage />} />
              <Route path="/health-nexus" element={<HealthNexusPage />} />
              <Route path="/academy" element={<AcademyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/press" element={<PressPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/investors" element={<InvestorsPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/api-reference" element={<APIReferencePage />} />
              <Route path="/status" element={<StatusPage />} />

              {/* AegisOS Documentation */}
              <Route path="/aegisos/quickstart" element={<AegisOSQuickstart />} />
              <Route path="/aegisos/commands" element={<AegisOSCommandRef />} />
              <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms-of-service" element={<TermsOfService />} />
              <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
              <Route path="/legal/user-agreement" element={<UserAgreement />} />
              <Route path="/legal/copyright" element={<CopyrightPolicy />} />
              <Route path="/privacy" element={<Navigate to="/legal/privacy-policy" replace />} />
              <Route path="/terms" element={<Navigate to="/legal/terms-of-service" replace />} />
              <Route path="/cookies" element={<Navigate to="/legal/cookie-policy" replace />} />

              {/* Auth */}
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />

              {/* Unified Portal Hub */}
              <Route
                path="/portal"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin", "client"]}>
                    <PortalDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Portal - RMS (Staff Portal) */}
              <Route
                path="/portal/rms"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin", "client", "hr"]}>
                    <PortalRMS />
                  </ProtectedRoute>
                }
              />

              {/* Portal - HR Dashboard */}
              <Route
                path="/portal/hr"
                element={
                  <ProtectedRoute allowedRoles={["hr", "admin"]}>
                    <HRDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Portal - Admin Panel */}
              <Route
                path="/portal/admin"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Portal - Admin Content Manager */}
              <Route
                path="/portal/admin/content"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminContentManager />
                  </ProtectedRoute>
                }
              />

              {/* Portal - Health Nexus AI */}
              <Route
                path="/portal/health-nexus"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin", "client"]}>
                    <HealthNexusPortal />
                  </ProtectedRoute>
                }
              />

              {/* Portal - Academy */}
              <Route
                path="/portal/academy"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin", "client"]}>
                    <AcademyPortal />
                  </ProtectedRoute>
                }
              />

              {/* Client Admin Dashboard */}
              <Route
                path="/portal/client-admin"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientAdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* ── New Portal Feature Routes ─────────────────────────────── */}

              {/* Staff: Leave Management */}
              <Route
                path="/portal/leave"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <LeaveManagementPage />
                  </ProtectedRoute>
                }
              />

              {/* All: Announcements */}
              <Route
                path="/portal/announcements"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin", "client"]}>
                    <AnnouncementsPage />
                  </ProtectedRoute>
                }
              />

              {/* Staff: Document Vault */}
              <Route
                path="/portal/documents"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <DocumentVaultPage />
                  </ProtectedRoute>
                }
              />

              {/* Staff: Expense Claims */}
              <Route
                path="/portal/expenses"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin"]}>
                    <ExpenseClaimsPage />
                  </ProtectedRoute>
                }
              />

              {/* Client: Support Tickets */}
              <Route
                path="/portal/support"
                element={
                  <ProtectedRoute allowedRoles={["client", "admin", "staff"]}>
                    <SupportTicketsPage />
                  </ProtectedRoute>
                }
              />

              {/* Client: Billing & Invoices */}
              <Route
                path="/portal/billing"
                element={
                  <ProtectedRoute allowedRoles={["client", "admin"]}>
                    <BillingPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin: Platform Analytics */}
              <Route
                path="/portal/analytics"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <PlatformAnalyticsPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin: Audit Logs */}
              <Route
                path="/portal/audit-logs"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AuditLogPage />
                  </ProtectedRoute>
                }
              />

              {/* Org Chart */}
              <Route
                path="/portal/org-chart"
                element={
                  <ProtectedRoute allowedRoles={["admin", "hr", "staff"]}>
                    <OrgChartPage />
                  </ProtectedRoute>
                }
              />

              {/* AEGIS-X Security Platform - Public landing + authenticated dashboard */}
              <Route path="/aegis-x" element={<AegisXDashboard />} />
              <Route path="/aegis-x/dashboard" element={<Navigate to="/aegis-x" replace />} />
              <Route
                path="/aegis-x/devices"
                element={
                  <ProtectedRoute allowedRoles={["admin", "staff", "client"]}>
                    <AegisXDevices />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aegis-x/threats"
                element={
                  <ProtectedRoute allowedRoles={["admin", "staff", "client"]}>
                    <AegisXThreats />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aegis-x/incidents"
                element={
                  <ProtectedRoute allowedRoles={["admin", "staff", "client"]}>
                    <AegisXIncidents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aegis-x/predator"
                element={
                  <ProtectedRoute allowedRoles={["admin", "staff", "client"]}>
                    <AegisXPredator />
                  </ProtectedRoute>
                }
              />

              {/* Autonomous Agents System - redirect to overview for authenticated admin/client users */}
              <Route
                path="/agents"
                element={
                  <AgentsSmartRedirect />
                }
              />

              {/* Autonomous Agents System - Admin/Client Access */}
              <Route
                path="/agents/overview"
                element={
                  <ProtectedRoute allowedRoles={["admin", "client"]}>
                    <AgentsOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents/workforce"
                element={
                  <ProtectedRoute allowedRoles={["admin", "client"]}>
                    <AgentsWorkforce />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents/approvals"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AgentsApprovals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents/activity"
                element={
                  <ProtectedRoute allowedRoles={["admin", "client"]}>
                    <AgentsActivity />
                  </ProtectedRoute>
                }
              />

              {/* The Delegation - AI Workforce Command Center */}
              <Route
                path="/agents/delegation"
                element={
                  <ProtectedRoute allowedRoles={["admin", "client"]}>
                    <TheDelegation />
                  </ProtectedRoute>
                }
              />

              {/* Projects */}
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <ProjectsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <ProjectDetailsPage />
                  </ProtectedRoute>
                }
              />

              {/* Health Nexus — Patient Portal */}
              <Route
                path="/patient"
                element={
                  <ProtectedRoute allowedRoles={["patient", "admin"]}>
                    <PatientDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/ai-doctor"
                element={
                  <ProtectedRoute allowedRoles={["patient", "admin"]}>
                    <AIDoctorChat />
                  </ProtectedRoute>
                }
              />
              {/* Alias so old /ai-doctor links still work */}
              <Route path="/ai-doctor" element={<Navigate to="/patient/ai-doctor" replace />} />

              {/* Health Nexus — Doctor Portal */}
              <Route
                path="/doctor"
                element={
                  <ProtectedRoute allowedRoles={["doctor", "admin"]}>
                    <DoctorDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Payment Success — accessible to any authenticated user */}
              <Route
                path="/payment-success"
                element={
                  <ProtectedRoute>
                    <PaymentSuccess />
                  </ProtectedRoute>
                }
              />

              {/* Legacy RMS Routes - Redirect to new portal */}
              <Route
                path="/rms"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin", "client"]}>
                    <Navigate to="/portal/rms" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rms/attendance"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin", "client"]}>
                    <Navigate to="/portal/rms" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rms/payslips"
                element={
                  <ProtectedRoute allowedRoles={["staff", "admin", "client"]}>
                    <Navigate to="/portal/rms" replace />
                  </ProtectedRoute>
                }
              />

              {/* Legacy Admin Route - Redirect to new portal */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <Navigate to="/portal/admin" replace />
                  </ProtectedRoute>
                }
              />

              {/* 404 — proper not-found page so Google doesn't see a Soft 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </Suspense>
      </PageTransition>
    </AnimatePresence>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <SplashDismisser />
        <UXLayer />
        <CommandPalette />
        <div className="min-h-screen gradient-bg">
          <AppRoutes />
          <GoogleAdSense />
          <Toaster position="top-right" richColors />
          <CookieBanner />
          <MaintenancePopup />
          <PagePopupManager />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
